import { Moment } from "moment";
import * as moment from "moment";
import * as _ from "lodash";
import { KeyValueBase } from "./models/interface";
import { InternationalisationService } from "../forked/angular-internationalisation";

export default class Utils {
  // wichtige Konstanten
  /** max width of 4K */
  public static readonly MaxWidth = 4096;
  /** max height of 4K */
  public static readonly MaxHeight = 2160;
  /** minimal yourapp Date is 1.1.1900 (ATTENTION! this date is relevant on the YourAPPServer) */
  public static readonly MinYMD = 19000101; // 
  /** minimal yourapp Time is 0:00 (ATTENTION! this time is relevant on the YourAPPServer) */
  public static readonly MinHMS = 0;

  /** is null or undefined */
  public static IsNull(value: any): boolean {
    return _.isNil(value);
  }

  /** string is null or empty */
  public static IsNullOrEmpty(value: string): boolean {
    return _.isNil(value) || value == "";
  }

  /** string is null or whitespace */
  public static IsNullOrWhiteSpace(value: string): boolean {
    return _.isNil(value) || value.replace(/\s/gi, '').length < 1;
  }

  /** prüft auf inhaltliche Gleichheit */
  public static IsEqual(value: any, other: any): boolean {
    return _.isEqual(value, other);
  }

  /**
   * Überprüft,ob ein übergebens Array nicht null, undefined und nicht leer ist.
   */
  public static ValidateArray<ArrayType>(data: ArrayType[]) {
    return data !== null && data !== undefined && data.length > 0;
  }

  /** Ermittelt die physikalische Bildshirmbreite (unter berüksichtigung der PixelRatio) */
  public static GetPhysicalScreenWidth(): number {
    if (typeof window !== "undefined") {
      return window.screen.width * window.devicePixelRatio;
    } else {
      return Utils.MaxWidth;
    }
  }

  /** Ermittelt die physikalische Bildshirmhöhe (unter berüksichtigung der PixelRatio) */
  public static GetPhysicalScreenHeight(): number {
    if (typeof window !== "undefined") {
      return window.screen.height * window.devicePixelRatio;
    } else {
      return Utils.MaxHeight;
    }
  }

  /** Ermittelt die Browserview-breite (unter berüksichtigung der PixelRatio) */
  public static GetViewInnerWidth(): number {
    if (typeof window !== "undefined") {
      return window.innerWidth * window.devicePixelRatio;
    } else {
      return Utils.MaxWidth;
    }
  }

  /** Ermittelt die Browserview-höhe (unter berüksichtigung der PixelRatio) */
  public static GetViewInnerHeight(): number {
    if (typeof window !== "undefined") {
      return window.innerHeight * window.devicePixelRatio;
    } else {
      return Utils.MaxHeight;
    }
  }

  /** Ermittelt die Breite der APP (im Browser ohne den "Rand") (ohne Berücksichtigung der PixelRatio)*/
  public static GetAPPLogicalWidth(fallbackWidth: number): number {
    const bodyElement = document.body;
    if (!_.isNil(bodyElement)) {
      return bodyElement.clientWidth;
    } else {
      return fallbackWidth;
    }
  }

  /** Ermitlung des Namen eines Enum Keys zum gesuchten Wert
   * @returns Key or undefine, if value not in enumtype
   */
  public static EnumName(enumtype: {}, value: any): string {
    return Object.keys(enumtype).find(key => enumtype[key] === value);
  }

  /** Wandelt bis zu zwei Integer YMD und HMS zu einem Moment
   * @param YMD Datum als int im Format YYYYMMDD
   * @param HMS Zeit im Format HHMMSS
   * @returns Jetzt mit übergebenen Datum (wenn nicht kleiner 1) und übergebener Uhrzeit (wenn nicht kleiner 0)
   */
  public static GetMomentFromInt(YMD: number, HMS: number = -1): Moment {
    const mom = moment();
    if (YMD < Utils.MinYMD) {
      YMD = Utils.MinYMD;
    }
    if (HMS < Utils.MinHMS) {
      HMS = Utils.MinHMS;
    }

    mom.year(YMD / 10000 % 10000)
      .month(((YMD / 100) % 100) - 1) // -1: month is 0 based
      .date(YMD % 100);
    mom.hours((HMS / 10000) % 100)
      .minutes((HMS / 100) % 100)
      .seconds(HMS % 100);
    return mom;
  }

  /** Integer YMD aus Moment ermitteln */
  public static GetYMDFromMoment(mom: Moment): number {
    return parseInt(mom.format("YYYYMMDD"), 10);
  }

  /** Integer HMS aus Moment ermitteln */
  public static GetHMSFromMoment(mom: Moment): number {
    return parseInt(mom.format("HHmmss"), 10);
  }

  /** return only the date part of the moment date time string */
  public static GetDateFromMomentString(date: string) {
    return moment(date).format("YYYYMMDD");
  }

  /** Verwendet die jQuery Extend Funktion, um das Object zu erweitern */
  public static GetExtend<T>(obj: T, extension: any): T {
    return Utils.DeepExtend(obj, extension);
  }

  /** erweitert das Object, dabei werden die Typen beachten, und getter, ohne ein setter, erzeugen kein Fehler */
  public static DeepExtend<T>(obj: T, extension: any): T {
    if (Utils.IsNull(extension)) {
      return obj; // is extension null or undefined, do not change the obj
    }
    // obj and extension must be objects
    if (typeof obj !== "object") {
      throw new Error("obj is not an Object");
    }
    if (typeof extension !== "object") {
      throw new Error("extension is not an Object");
    }

    const extensionDescriptors = Object.getOwnPropertyDescriptors(extension);

    for (const DescriptorKey in extensionDescriptors) {
      if (extensionDescriptors.hasOwnProperty(DescriptorKey)) {
        const extensionVal = Utils.getExtendProperty(extensionDescriptors[DescriptorKey]);
        if (extensionVal !== undefined) {
          Utils.setObjProperty(obj, DescriptorKey, extensionVal);
        }
      }
    }

    return obj;
  }

  /** Sets or Create the Property by propKey of the obj with the val
   * @param obj object for seting the property
   * @param propKey propertyname
   * @param val value for setting, can be a struct, object or array (object and array create a deep copy)
   */
  private static setObjProperty(obj: {}, propKey: string, val: any): void {
    const objDescriptor = Object.getOwnPropertyDescriptor(obj, propKey);
    if (objDescriptor === undefined) {
      obj[propKey] = val; // create new property
    } else if (Utils.IsNull(obj[propKey])) {
      obj[propKey] = val; // old value is null, direct copy 
    } else if (objDescriptor.value !== undefined) {
      if (!Array.isArray(objDescriptor.value) && typeof objDescriptor.value === "object") {
        Utils.DeepExtend(obj[propKey], val);
      } else {
        obj[propKey] = val;
      }
    } else if (objDescriptor.set !== undefined) {
      debugger // test is set realy sets the value
      objDescriptor.set(val);
    } else {
      // do nothing, getter has no setting params
    }
  }

  /** Gets the value to extend from PropertyDescriptor
   * @param extensionDescriptor PropertyDescriptor to gets the value
   */
  private static getExtendProperty(extensionDescriptor: PropertyDescriptor): any {
    let extensionVal: any;

    if (extensionDescriptor.value !== undefined) {
      extensionVal = extensionDescriptor.value;
    } else if (extensionDescriptor.get && extensionDescriptor.set) {
      extensionVal = extensionDescriptor.get();
    } else {
      extensionVal = undefined;
    }

    return Utils.getExtendValueCopy(extensionVal);
  }

  /** Returns a copy of the given value
   * @param extensionValue struct, object or array
   */
  private static getExtendValueCopy(value: any): any {
    let val: any;

    if (value === undefined) {
      val = undefined;
    } else if (Array.isArray(value)) {
      val = [];
      // tslint:disable-next-line: forin // extensionVal is an array
      for (const key in value) {
        val[key] = Utils.getExtendValueCopy(value[key]);
      }
    } else if (value === null) {
      val = null;
    } else if (typeof value === "object") {
      val = Utils.GetCopy(value);
    } else {
      val = value;
    }

    return val;
  }

  /** Verwendet die DeepExtend Funktion, um eine tiefe Kopie des Objects zu erzeugen und erhält den Prototype */
  public static GetCopy<T>(obj: T): T {
    // return a deep copy of the object/ it create the object of equal prototype
    if (obj) {
      const newProt = Object.create(Object.getPrototypeOf(obj));
      return Utils.DeepExtend(newProt, obj);
    } else {
      return null;
    }
  }

  /** Wandelt ein EnumType in ein KeyValueBase[] */
  public static GetListFromEnum(enumType: {}): KeyValueBase[] {
    const result: KeyValueBase[] = [];
    for (const key in enumType) {
      if (typeof enumType[key] === "number") {
        const keyValueBase: KeyValueBase = {
          key: key,
          value: enumType[key]
        };
        result.push(keyValueBase);
      }
    }
    return result;
  }

  // ! Das gehürt nicht in die Utils Funktion
  public static GetGenderListFromEnum(enumType: {}) {
    const result: KeyValueBase[] = [];
    for (const key in enumType) {
      if (typeof enumType[key] === "number") {
        const keyValueBase: KeyValueBase = {
          key: `phGender_${key}`,
          value: enumType[key]
        };
        result.push(keyValueBase);
      }
    }
    return result;
  }

  // public static groupBy<T extends any, K extends keyof T>(array: T[], key: K | { (obj: T): string }): Record<string, T[]> {
  //   const keyFn = key instanceof Function ? key : (obj: T) => obj[key];
  //   return array.reduce(
  //     (objectsByKeyValue, obj) => {
  //       const value = keyFn(obj);
  //       objectsByKeyValue[value] = (objectsByKeyValue[value] || []).concat(obj);
  //       return objectsByKeyValue;
  //     },
  //     {} as Record<string, T[]>
  //   );
  // }

  /** Liest die Major Version aus, wenn es diese nicht findet returnt es die defaultNumber */
  public static GetNumberFromVersion(version: string, defaultNumber: number): number {
    const numericCharacters = ".0123456789";
    let numericString = "";
    let firstNumericCharacter = false;
    let firstComma = false;
    for (let c of version) {
      if ((numericCharacters.indexOf(c) > -1) && ((firstNumericCharacter) || ((!firstNumericCharacter) && (c !== "."))) && ((!firstComma) || (c !== "."))) {
        firstNumericCharacter = true;
        firstComma = firstComma || (c === ".");
        numericString += c;
      } else {
        if (firstNumericCharacter) {
          break;
        }
      }
    }
    const number = parseFloat(numericString);
    if (isNaN(number)) {
      return defaultNumber;
    } else {
      return number;
    }
  }

  /** Asynchrone foreach eines Arrays */
  public static async asyncForEach<T>(array: Array<T>, callback: (entry: T, index?: number, array?: Array<T>) => {}) {
    for (let i = 0; i < array.length; ++i) {
      await callback(array[i], i, array);
    }
  }

  /** Asynchrones filtern eines Arrays */
  public static async asyncFilter<T>(array: Array<T>, callback: (entry: T, index?: number, array?: Array<T>) => {}): Promise<Array<T>> {
    const results = await Promise.all(array.map(callback));
    return array.filter((val, index) => results[index]);
  }

  /** Asynchrones mappen eines Arrays */
  public static async asyncMap<T, U>(array: Array<T>, callback: (entry: T, index?: number, array?: Array<T>) => U): Promise<Array<U>> {
    const results = await Promise.all(array.map(callback));
    return results;
  }

  /** wartet im Promise für x Millisekunden */
  public static wait(milliseconds): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, milliseconds));
  }

  /**
   * Shuffles array in place. ES6 version
   * @param {Array} array items An array containing the items.
   */
  public static shuffle<T>(array: Array<T>): Array<T> {
    for (let i = array.length - 1; i > 0; --i) {
      const j = Math.floor(Math.random() * (i + 1));
      [array[i], array[j]] = [array[j], array[i]];
    }
    return array;
  }

  /** Formatiert Cents zu einem Betrag */
  public static centsToCurrency(price_in_cents: number): number {
    return Utils.moneyRound(price_in_cents / 100);
  }

  /** Formatiert den Preis als Währung optional mit Währungssymbol und Tausendertrenner (auto moneyRound)
   * mit berücksichtigung der Sprache de/en
   */
  public static currencyFormatter(price: number, symbol: string = "", hasSymbol: boolean = true, useGrouping: boolean = true): string {
    let formattedPrice = "";
    price = Utils.moneyRound(price);
    const numberFormatOptions: Intl.NumberFormatOptions = {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
      useGrouping: useGrouping,
    };
    const currentLangang = InternationalisationService.locale;
    if (currentLangang === "de") {
      formattedPrice = `${price.toLocaleString("de-DE", numberFormatOptions)} ${hasSymbol ? this.setSufixCurrency(symbol) : ""}`;
    } else {
      formattedPrice = `${price.toLocaleString("en-GB", numberFormatOptions)} ${hasSymbol ? this.setSufixCurrency(symbol) : ""}`;
    }
    return formattedPrice.trim();
  }

  /** returnt das Symbol, sonst das '€' Symbol */
  private static setSufixCurrency(symbol: string): string {
    let sufixSymbol: string = "";
    if (symbol) {
      sufixSymbol = symbol;
    } else {
      sufixSymbol = "€";
    }
    return sufixSymbol;
  }

  /** Prüfung ob an der Position im Bitfeld(as number) der bit gesetzt ist */
  public static isBitInBitfieldSet(bitfield: number, position: number): boolean {
    // tslint:disable-next-line: no-bitwise
    return (bitfield & (1 << (position))) !== 0;
  }

  /** is null or undefine wird es zum Leerstring */
  public static NullToEmptyString(value: string): string {
    return _.isNil(value) ? "" : value;
  }

  /** Rundet auf zwei Nachkommastellen */
  public static moneyRound = (amount: number): number => { // Todo think about it (look how it's done in the portal too)
    const rounded = Math.round(amount * 100) / 100;
    return rounded === -0 ? 0 : rounded; // Normalize -0 to 0 (avoid -0,00€)
  }

  /** Replaces Text In html
   * @param text string to replace in. i.e. "Hello {0}"
   * @param replacements
   * @param replacements[].target string to replace. i.e "{0}"
   * @param replacements[].replace string to replace. i.e "World"
   * @param replacements[].class if class exists replacement is wrapped in a span with this as the class. i.e "bold"
   * Example replaceInHTML("Hello {0}", [{target:"{0}", replace:"World", "bold"}]) = 'Hello <span class="bold">World</span>'
  */
  public static replaceInHTML(text: string, replacements: { target: string, replace: string, class?: string }[] = null): string {
    if (replacements && replacements.length > 0) {
      let str = text;
      replacements.forEach(replacement => {
        if (replacement.class != null) {
          str = str.replace(replacement.target, `<span class="${replacement.class}">${replacement.replace}</span>`);
        } else {
          str = str.replace(replacement.target, replacement.replace);
        }
      });
      return str;
    }
  }

  /** Nummer wird zum sting mit führenden nullen um die size zu füllen */
  public static padLeft(num: number, size: number): string {
    let s = num + "";
    while (s.length < size) {
      s = "0" + s;
    };
    return s;
  }

  /** Prüft ob das Datum zwichen MinYMD und heute (systemdatum) liegt */
  public static isValidateDate(year: number, month: number, day: number): boolean {
    const me = this;
    const MaxYMD = parseInt(moment().format("YYYYMMDD"), 10);
    const YMDData = (year * 10000) + ((month + 1) * 100) + (day * 1);
    if (me.MinYMD > YMDData || YMDData > MaxYMD) {
      return false;
    } else {
      const date = new Date(year, month, day);
      return date.getFullYear() === year && date.getMonth() === month && date.getDate() === day;
    }
  }

  /**
   * Checks if a given date is in the past (before today).
   * Ignore time components, only compare the date part.
   * @param date The date to check
   * @returns True if the date is before today, false otherwise
   */
  public static isPastDate(date: Date): boolean {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const checkDate = new Date(date);
    checkDate.setHours(0, 0, 0, 0);
    return checkDate < today;
  }


  /**
 * Converts degrees to radians.
 * 
 * @param degrees Number of degrees.
 */
  private static degreesToRadians(degrees) {
    return degrees * Math.PI / 180;
  }

  /**
  * Returns the distance between 2 points of coordinates in Kilometer
  * 
  * @see https://stackoverflow.com/questions/365826/calculate-distance-between-2-gps-coordinates [http://www.movable-type.co.uk/scripts/latlong.html] (old: https://stackoverflow.com/a/1502821/4241030)
  * @param lat1 Latitude of the point A
  * @param lng1 Longitude of the point A
  * @param lat2 Latitude of the point B
  * @param lng2 Longitude of the point B
  */
  public static getDistanceBetweenPoints(lat1, lng1, lat2, lng2): number {
    const me = this;
    // The radius of the planet earth in kilometers
    const R = 6372.797; // min 6357 to max 6378 (6372.797 ist ein mittel maß)
    const sinLat = Math.sin(me.degreesToRadians(lat2 - lat1) / 2);
    const sinLng = Math.sin(me.degreesToRadians(lng2 - lng1) / 2)
    const a = Math.pow(sinLat, 2)
      +
      Math.cos(me.degreesToRadians(lat1))
      *
      Math.cos(me.degreesToRadians(lat2))
      *
      Math.pow(sinLng, 2);

    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    const distance = R * c;

    return distance;
  }

  /**
   * Decodes HTMLEntities in a String (i.e &#252 => ü &#times => x (Symbol))
   * @param str string with HTMLEntities
   */
  public static decodeHTMLEntities(str: string) {
    const txtarea = document.createElement("textarea");
    // DoubleDecode for &amp;#215; structures
    txtarea.innerHTML = str;
    txtarea.innerHTML = txtarea.value;
    return txtarea.value;
  }

  /** Price per unit  */
  public static pricePerUnit(basePrice: number, baseFactor: number): number {
    if (baseFactor != 0) {
      return Utils.moneyRound(basePrice / baseFactor);
    } else {
      return 0;
    }
  }

  /** Currency Symbol as Iso code */
  public static mapCurrencyToIsoCode(currencySymbol: string): string {
    const currency = { "€": "EUR", "$": "USD", "£": "GBP" }[currencySymbol];
    return currency;
  }

  /**
  * Replaces JSON link placeholders (e.g. {"Label":"URL"}) in the newsletter text
   * with HTML anchor tags that open in a new tab.
   * Example:  {"Terms":"https://example.com"}
   * 
   * converts into HTML links like:
   * <a href="https://example.com" target="_blank">Terms</a>
   *
   * @param text - The raw newsletter text received from the backend.
   * @returns The processed text with HTML anchor tags instead of JSON placeholders.
   */
  public static replaceJsonLinks(text: string): string {

    const regex = /\{.*?\}/g;

    return text.replace(regex, (placeholder) => {

      try {

        const parsed = JSON.parse(placeholder);
        const label = Object.keys(parsed)[0];
        const url = parsed[label];

        return `<a href="${url}" target="_blank">${label}</a>`;

      } catch {
        return placeholder;
      }

    });

  }
}
